import { useState, useRef } from "react";
import axios from "axios";
import { UploadSimple, X, Image as ImageIcon, Spinner } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";

import { BACKEND_URL, API } from "@/config";

const ImageUpload = ({ value, onChange, label, placeholder = "https://...", compact = false }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    const allowed = ["image/png", "image/jpeg", "image/gif", "image/webp", "image/svg+xml"];
    if (!allowed.includes(file.type)) {
      alert("Please upload an image file (PNG, JPG, GIF, WebP, SVG)");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert("File too large (max 10MB)");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload`, formData);
      onChange(res.data.url);
    } catch (err) {
      console.error("Upload failed:", err);
      alert("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  const onFileSelect = (e) => {
    const file = e.target.files[0];
    handleFile(file);
    e.target.value = "";
  };

  const clearImage = () => {
    onChange("");
  };

  if (value) {
    return (
      <div className="space-y-2" data-testid={`image-upload-${label?.toLowerCase().replace(/\s/g, "-") || "field"}`}>
        <div className="relative group rounded-md border border-zinc-200 overflow-hidden bg-zinc-50">
          <img
            src={value}
            alt={label || "Uploaded"}
            className={compact ? "w-full h-20 object-contain" : "w-full h-32 object-contain"}
            onError={(e) => {
              e.target.src = "";
              e.target.alt = "Failed to load";
              e.target.className = "hidden";
            }}
          />
          <button
            type="button"
            onClick={clearImage}
            className="absolute top-1 right-1 bg-white/90 hover:bg-red-50 border border-zinc-300 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid="image-upload-clear-btn"
          >
            <X size={14} className="text-red-500" />
          </button>
        </div>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="text-xs"
          data-testid="image-upload-url-input"
        />
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={`image-upload-${label?.toLowerCase().replace(/\s/g, "-") || "field"}`}>
      <div
        className={`border-2 border-dashed rounded-md flex flex-col items-center justify-center cursor-pointer transition-colors ${
          dragOver
            ? "border-[var(--brand)] bg-[var(--brand-tint-5)]"
            : "border-zinc-300 hover:border-zinc-400 bg-zinc-50"
        } ${compact ? "py-3 px-2" : "py-6 px-4"}`}
        onClick={() => !uploading && fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        data-testid="image-upload-dropzone"
      >
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onFileSelect}
          data-testid="image-upload-file-input"
        />
        {uploading ? (
          <>
            <Spinner size={compact ? 20 : 28} className="text-[var(--brand)] animate-spin" />
            <span className="text-xs text-zinc-500 mt-1">Uploading...</span>
          </>
        ) : (
          <>
            {compact ? (
              <UploadSimple size={20} className="text-zinc-400" />
            ) : (
              <>
                <UploadSimple size={28} className="text-zinc-400 mb-1" />
                <span className="text-xs text-zinc-500">Click or drag image here</span>
                <span className="text-xs text-zinc-400 mt-0.5">PNG, JPG, GIF, WebP (max 10MB)</span>
              </>
            )}
          </>
        )}
      </div>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="text-xs"
        data-testid="image-upload-url-input"
      />
    </div>
  );
};

export default ImageUpload;
